import { Feature, IUserProfileModel, License, OrgLicense, UserType, orgAdminData } from 'types/ProfileTypes';
import i18next, { t } from 'i18next';

import { AxiosInstance } from 'axios';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';
import { INotificationsStore } from './NotificationsStore';
import { IResult } from 'services/Result';
import { makeAutoObservable } from 'mobx';
import { OrganizationLicenses } from "../types/models/OrganizationLicenses";

export interface IProfileStore {
  userId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  photoUrl: string | null;
  isLoggingOut: boolean;
  authority: string;

  preferredLanguage: string;
  preferredUnitSystem: string;

  loaded: boolean;

  userType: UserType | null;
  activeLicenses: OrganizationLicenses | null;

  playerId: string | null;

  ensureDataAvailability(apiInstance: AxiosInstance): Promise<void>;

  // custom methods
  updateProfile(apiInstance: AxiosInstance, data): Promise<void>;
  isPlayer(): boolean;
  isUserType(userType: UserType): boolean;
  HasAnyLicenseOf(requiredLicenses?: License[] | License): boolean;
  runActionIfLicenseIsValid(license: License, action: () => void): void;
  HasFeature(requiredFeature?: Feature): boolean;
  HasAnyFeatureOf(requiredFeatures?: Feature[] | Feature): boolean;
  setLogoutAction(value: boolean): void;
  setUserData(data: IUserProfileModel): void;
  setUnknownUser(): void;
  LoadLicenseData(apiInstance: AxiosInstance): Promise<void>;
}

export class ProfileStore implements IProfileStore {
  userId: string = '';
  firstName: string = '';
  lastName: string = '';
  fullName: string = '';
  email: string = '';
  photoUrl: string | null = null;
  isLoggingOut: boolean = false;
  authority: string = '';

  preferredLanguage: string = 'en';
  preferredUnitSystem: string = 'imperial';

  public loaded: boolean = false;

  userType: UserType | null = null;
  activeLicenses: OrganizationLicenses | null = null;

  // organization admins
  organizationId: string | null = null;
  organizationName: string;
  organizationShortName: string;
  organizationAdmins: orgAdminData[];
  organizationAdminEmail: string;
  playerId: string | null = null;

  _notificationStore: INotificationsStore;
  constructor(notificationStore: INotificationsStore) {
    this._notificationStore = notificationStore;
    makeAutoObservable(this);
  }

  public runActionIfLicenseIsValid = (license: License, action: () => void) => {
    if (this.HasAnyLicenseOf(license)) {
      this._notificationStore.displayNotification({
        title: t('licenseExpired.ToastTitle'),
        message: t('licenseExpired.ToastMessage'),
        variant: 'error',
      });
      return;
    }

    action();
  };

  setLogoutAction(value: boolean) {
    this.isLoggingOut = value;
  }

  setUserData(data: IUserProfileModel) {
    this.userId = data.userId;
    this.email = data.email;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.photoUrl = data.profilePhotoUrl;
    this.preferredLanguage = data.preferredLanguage;
    this.preferredUnitSystem = data.preferredUnitSystem || 'imperial';

    if (!this.firstName && !this.lastName) {
      this.fullName = '';
    } else {
      this.fullName = (data.firstName == null ? '' : data.firstName + ' ') + (data.lastName || '');
    }
    this.loaded = true;
  }

  setUnknownUser() {
    this.loaded = false;
    this.fullName = i18next.t('unknownUser');
  }

  public async ensureDataAvailability(apiInstance: AxiosInstance) {
    if (this.loaded || this.userId) {
      return;
    }
    await this.LoadData(apiInstance);
    await this.LoadLicenseData(apiInstance);
  }

  public async updateProfile(apiInstance: AxiosInstance, data, success = () => {}) {
    await this.SaveData(apiInstance, data, success);
  }

  private async LoadData(apiInstance: AxiosInstance) {
    const response = await apiInstance.get<IResult<IUserProfileModel>>('/account/profile');
    if (response && response.data && !response.data.isError && response.data.data) {
      this.setUserData(response.data.data);
    } else {
      this.setUnknownUser();
    }
  }
  
  public async LoadLicenseData(apiInstance: AxiosInstance) {
    let response = await apiInstance.get<IResult<OrganizationLicenses>>('api/Organization/licenses');
    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) {
      this.activeLicenses = response.data.data
    } 
  }

  private async SaveData(apiInstance: AxiosInstance, data, success = () => {}) {
    let response = await apiInstance.put('account/profile', {
      firstName: data.firstName,
      lastName: data.lastName,
      userType: data.userType,
      preferredLanguage: data.preferredLanguage,
      preferredUnitSystem: data.preferredUnitSystem,
    });
    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      await this.LoadData(apiInstance);
      success();
      return true;
    }
    return false;
  }

  // custom methods
  isPlayer = () => {
    return !!this.isUserType(UserType.Player);
  };

  isUserType = (userType: UserType) => {
    return !!(this.userType && this.userType.toLowerCase() === userType.toLowerCase());
  };

  public HasAnyLicenseOf(requiredLicenses?: License[] | License): boolean {
    if (!this.activeLicenses || !this.activeLicenses.licenses) {
      return false;
    }

    if (requiredLicenses instanceof Array) {
      return requiredLicenses.some((rl) => this.activeLicenses?.licenses.includes(rl.toString()));
    }

    return !requiredLicenses || this.activeLicenses.licenses.includes(requiredLicenses.toString());
  }

  public HasFeature(requiredFeature?: Feature): boolean {
    if (!requiredFeature || !this.activeLicenses || !this.activeLicenses.features) {
      return false;
    }

    return this.activeLicenses.features.includes(requiredFeature.toString());
  }

  public HasAnyFeatureOf(requiredFeatures?: Feature[] | Feature): boolean {
    if (requiredFeatures instanceof Array) {
      return requiredFeatures.some((f) => this.HasFeature(f));
    }

    return this.HasFeature(requiredFeatures);
  }
}
